import { useContext, useEffect } from "react";
import { Box } from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { FiltersContext } from "../store/filters-options-context";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SyrianFlag from "../assets/Flag_of_Syria.png";
import { fuzzySearchRanked } from '../utils/fuzzySearch';

export default function ComboFilters({
    selectId,
    selectLabel,
    urlDomain,
    urlArea,
    handleNext,
    urlTheme,
    showIndicators,
}) {
    const { selectOptions, valueSelected, handleChange, setValueSelected, itemsList } = useContext(FiltersContext);

    const options = (selectOptions[selectId] || []).sort((a, b) => (a.label > b.label ? 1 : -1));

    useEffect(() => {
        if (urlDomain) {
            const lowerUrl = String(urlDomain).toLowerCase();
            const matchedOption = options.find((option) => {
                const byValue = option.value && String(option.value).toLowerCase() === lowerUrl;
                const byLabel = option.label && String(option.label).toLowerCase() === lowerUrl;
                return byValue || byLabel;
            });

            if (matchedOption) {
                setValueSelected((prev) => ({ ...prev, [selectId]: matchedOption }));
                handleChange({ target: { name: selectId, value: matchedOption } });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlDomain, options, setValueSelected, selectId]);

    useEffect(() => {
        if (urlTheme) {
            const matchedOption = options.find(
                (option) => option.slug && option.slug.toLowerCase() === urlTheme.toLowerCase()
            );

            if (matchedOption) {
                setValueSelected((prev) => ({ ...prev, [selectId]: matchedOption }));
                handleChange({ target: { name: selectId, value: matchedOption } });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTheme, options, setValueSelected, selectId]);


    useEffect(() => {
        if (urlArea) {
            const matchedOption = options.find(
                (option) => option.id === urlArea
            );

            if (matchedOption) {
                setValueSelected((prev) => ({ ...prev, [selectId]: matchedOption }));
                handleChange({ target: { name: selectId, value: matchedOption } });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlArea, options, setValueSelected, selectId]);


    return (
        <Autocomplete
            sx={{ width: "100%", margin: "15px 0 5px 0", display: options && options[0]?.label !== '' ? 'inline-block' : 'none' }}
            id={selectId}
            options={options}
            autoHighlight
            value={valueSelected ? valueSelected[selectId] : null}
            popupIcon={<ExpandMoreIcon />}
            onChange={(event, value) => {
                handleChange({ target: { name: selectId, value: value } });

                if (selectId === "themes" && value !== null && showIndicators) {
                    // Find the matching theme in itemsList and get its individual chart items
                    const matchingTheme = itemsList.find(item => item.slug === value.slug);
                    if (matchingTheme && matchingTheme.items) {
                        showIndicators(matchingTheme.items, value.slug, true);
                    }
                }

                if (value !== null) {
                    handleNext && handleNext();
                }
            }}
            isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                if (selectId === 'themes') return option.slug === value.slug;
                if (selectId === 'domain_code') return option.value === value.value;
                if (selectId === 'repositories') return option.id === value.id;
                if (selectId === 'area') return option.id === value.id;
                return option === value;
            }}
            getOptionLabel={(option) => (option && option.label) || ""}
            filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                return fuzzySearchRanked(inputValue, options, 'label');
            }}
            renderOption={
                selectId === "area"
                    ? (props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <Box
                                component="li"
                                key={key}
                                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                                {...otherProps}
                            >
                                <img
                                    loading="lazy"
                                    width="20"
                                    srcSet={
                                        option.code.toLowerCase() === "sy"
                                            ? `${SyrianFlag} 2x`
                                            : `https://flagcdn.com/w80/${option.code.toLowerCase()}.png 2x`
                                    }
                                    src={
                                        option.code.toLowerCase() === "sy"
                                            ? `${SyrianFlag}`
                                            : `https://flagcdn.com/w80/${option.code.toLowerCase()}.png`
                                    }
                                    alt=""
                                />
                                {option.label}
                            </Box>
                        );
                    }
                    : (props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <Box component="li" key={key} {...otherProps}>
                                {option.label}
                            </Box>
                        );
                    }
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={selectLabel}
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: "off",
                        autoCorrect: "off",
                        autoCapitalize: "off",
                        spellCheck: "false",
                    }}
                />
            )}
        />
    );
}
